import React, { useEffect, useState } from 'react';
import ModulesPanel from '../components/ModulesPanel';
import LoginAsPanel from '../components/LoginAsPanel';
import AdminOrgsPanel from '../components/AdminOrgsPanel';
import OrganizationsTable from '../components/OrganizationsTable';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import OrgOverviewModal from '../components/OrgOverviewModal';
import OrgEditModal from '../components/OrgEditModal';
import CGAdminConsole from './CGAdminConsole';
import MapleCoinOperations from './MapleCoin/operations/MapleCoinOperations';
import { useAuthentication } from '../context/AuthContext';
import './SuperAdminDashboard.css';

interface SubscriptionData {
  orgID: string;
  companyName: string;
  displayName: string;
  planName: string;
  planFeatures: Record<string, { enabled: boolean }>;
  overrideFeatures: Record<string, { enabled: boolean }>;
  lastUpdated: string;
  createdAt?: string;
  defaultModule?: string;
  billingAdmin?: string;
  ownerName?: string;
  ownerEmail?: string;
  lastAccessedAt?: string;
  lastAccessedBy?: string;
  lastAccessedByEmail?: string;
}

interface AddOn {
  key: string;
  name: string;
  description: string;
  category: string;
}

export default function PlatformAdminDashboard() {
  const { org } = useAuthentication();
  const [subscriptions, setSubscriptions] = useState<SubscriptionData[]>([]);
  const [addOns, setAddOns] = useState<AddOn[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [pendingDelete, setPendingDelete] = useState<{ orgID: string; companyName: string } | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [viewingOrgID, setViewingOrgID] = useState<string | null>(null);
  const [editingOrgID, setEditingOrgID] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<
    'modules' | 'orgs' | 'loginas' | 'adminorgs'
  >('orgs');

  const TABS: { key: typeof activeTab; label: string }[] = [
    { key: 'orgs', label: 'Organizations' },
    { key: 'modules', label: 'Modules' },
    { key: 'loginas', label: 'LoginAs' },
    { key: 'adminorgs', label: 'Admin Orgs' },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const addOnRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/add_on`, {
          method: 'GET',
          credentials: 'include',
        });
        if (!addOnRes.ok) throw new Error('Unable to retrieve add-on values');
        setAddOns(await addOnRes.json());

        const subRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/orgs`, {
          method: 'GET',
          credentials: 'include',
        });
        if (!subRes.ok) throw new Error('Unable to retrieve subscriptions.');
        setSubscriptions(await subRes.json());
      } catch (err) {
        setSubscriptions([]);
        setAddOns([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/admin/orgs/${encodeURIComponent(pendingDelete.orgID)}`,
        { method: 'DELETE', credentials: 'include' },
      );
      if (!res.ok) throw new Error('Failed to delete organization');
      setSubscriptions((prev) => prev.filter((s) => s.orgID !== pendingDelete.orgID));
      setPendingDelete(null);
    } catch (err) {
      console.error(err);
      alert('Failed to delete organization.');
    } finally {
      setDeleting(false);
    }
  };

  const handleOverrideChange = (orgID: string, key: string, value: boolean | null) => {
    setSubscriptions((prev) =>
      prev.map((sub) => {
        if (sub.orgID !== orgID) return sub;
        const updated = { ...sub, overrideFeatures: { ...sub.overrideFeatures } };
        if (value === null) {
          delete updated.overrideFeatures[key];
        } else {
          updated.overrideFeatures[key] = { enabled: value };
        }
        return updated;
      }),
    );
  };

  // The AskSmart platform SuperAdmin console is this page; any other admin org
  // (CGAdmin, …) that reaches platform-admin mode gets its OWN console instead
  // of the platform dashboard — otherwise CGAdmin would look identical to
  // AskAdmin. Routed by an explicit, opt-in match on the org's purpose key —
  // plus the exact "CGAdmin" label, for the original org created without one.
  // Every other admin org gets the coming-soon placeholder; it must never fall
  // through to another product's console. Add a new console here when it's built.
  //
  // MapleCoin_Operations routes on the session's mapleCoinOperations flag,
  // which the backend sets from the configured org ID — never from the org's
  // editable name, label or purpose. /maplecoin/ops enforces it independently.
  const isAskAdmin = org.orgID === process.env.REACT_APP_ASK_ADMIN_ORG_ID;
  if (!isAskAdmin) {
    if (org.mapleCoinOperations === true) return <MapleCoinOperations />;
    const purpose = (org.adminPurpose ?? '').trim().toLowerCase();
    const label = (org.adminLabel ?? '').replace(/\s+/g, '').toLowerCase();
    const isCaddieGamesAdmin =
      purpose.startsWith('caddiegames') || label === 'cgadmin';
    if (isCaddieGamesAdmin) return <CGAdminConsole />;
    const name = org.adminLabel || org.bannerName || 'This admin console';
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>{name}</h2>
        <p style={{ color: '#9aa0a6', fontSize: '1rem', marginTop: '2rem' }}>
          {name} will appear here soon.
        </p>
      </div>
    );
  }

  return (
    <div className="platform-admin-dashboard">
      <div className="sa-sticky-head">
        <div className="sa-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`sa-tab${activeTab === t.key ? ' sa-tab-active' : ''}`}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="sa-tab-panel">
        {activeTab === 'modules' && (
          <ModulesPanel subscriptions={subscriptions} embedded />
        )}
        {activeTab === 'orgs' && (
          <OrganizationsTable
            subscriptions={subscriptions}
            onView={(orgID) => setViewingOrgID(orgID)}
            onEdit={(orgID) => setEditingOrgID(orgID)}
            onDelete={(orgID, companyName) =>
              setPendingDelete({ orgID, companyName })
            }
          />
        )}
        {activeTab === 'loginas' && <LoginAsPanel embedded />}
        {activeTab === 'adminorgs' && <AdminOrgsPanel />}
      </div>

      {loading && (
        <div className="cdp-overlay">
          <div className="cdp-loading-spinner">Loading...</div>
        </div>
      )}

      <DeleteConfirmModal
        isOpen={!!pendingDelete}
        onClose={() => !deleting && setPendingDelete(null)}
        onConfirm={handleDelete}
        title="Delete Organization"
        message={
          pendingDelete
            ? `Permanently delete "${pendingDelete.companyName}"? This removes its subscription, entitlements, and customization. This cannot be undone.`
            : ''
        }
        confirmLabel={deleting ? 'Deleting...' : 'Delete'}
      />

      <OrgOverviewModal
        isOpen={!!viewingOrgID}
        onClose={() => setViewingOrgID(null)}
        org={subscriptions.find((s) => s.orgID === viewingOrgID) || null}
        addOns={addOns}
      />

      <OrgEditModal
        isOpen={!!editingOrgID}
        onClose={() => setEditingOrgID(null)}
        subscription={subscriptions.find((s) => s.orgID === editingOrgID) || null}
        addOns={addOns}
        onOverrideChange={handleOverrideChange}
      />
    </div>
  );
}
